import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../../../Components/Themed"
import type { MarketProps, OrderProps, PublicPlayerProps, TradeProps } from '../../../types';
import { MarketComponentApi, MarketComponentHelpers } from '../../api';
import { ActivityIndicator, FlatList, Image } from 'react-native';
import { useColors } from '../../../constants/useColors';
import Pagination from '../../../Components/Pagination';

type TradeListProps = {
    contest_id:string,
    contest_type:string,
    contest_title:string,
    order_ids:string[],
    market:MarketProps,
    onViewOrder:(order_id:string) => void,
    onViewTrade:(trade_id:string) => void,
    onViewPlayer:(player_id:string) => void,
    onClose:() => void
}

const TradeList = ({ contest_title, contest_id, contest_type, order_ids, market, onViewOrder, onViewPlayer, onViewTrade, onClose }:TradeListProps) => {
    const Colors = useColors();
    const [ order_list, setOrderList ] = useState<{
        players:PublicPlayerProps[],
        orders:OrderProps[]
    }>({
        players:[],
        orders:[]
    });
    const { orders, players } = order_list;
    const [ list_data, setListData ] = useState<{
        loading:boolean,
        trades:TradeProps[],
        offset:number
    }>({
        loading:false,
        trades:[],
        offset:0
    });
    const { loading, trades } = list_data;
    useEffect(() => {
        getOrders();
        getTradeData(0);
    },[market?.market_id]);

    const getOrders = async() => {
        const ords = await MarketComponentApi.getOrdersByIds(order_ids);
        const plys = await MarketComponentApi.getPlayersByIds([ ...new Set(ords.map(o => o.player_id)) ])
        setOrderList({
            players:plys,
            orders:ords
        })
    }

    const getTradeData = async(page:number) => {
        setListData({ ...list_data, loading: true });
        const trds = await MarketComponentApi.getTradesByEventAndMarket(contest_type, contest_id, market.market_id, page);
        setListData({ 
            ...list_data, 
            loading: false, 
            offset:page,
            trades: trds.filter(t => t.market_type == 'FOR_MONEY' && t.market_id == market.market_id && t.position_ids.length > 0),
        });
    }

    const renderTrades = (data:{ item:TradeProps, index:number }) => {
        if(data.item.position_ids.length == 0){ return <></> }
        const { found_orders, found_positions } = MarketComponentHelpers.getOrdersFromTrade(data.item, orders);
        if(found_orders.length != 2){ return <></> }
        let trade_side_order = found_orders.find(o => o.side == market.trade_side);
        let trade_side_position = found_positions.find(p => p.order_id == trade_side_order?.order_id);
        if(!trade_side_order || !trade_side_position){ return <></> }
        let o_side_order = found_orders.find(o => o.order_id != trade_side_order.order_id);
        let o_side_position = found_positions.find(p => p.order_id == o_side_order?.order_id);
        if(!o_side_order || !o_side_position){ return <></> }
        const o_side_player = players.find(p => p.player_id == o_side_order.player_id);
        const t_side_player = players.find(p => p.player_id == trade_side_order.player_id);
        const route_premium = (o_side_position.probability + trade_side_position.probability) - 1
        const revenue = (o_side_position.original_stake + trade_side_position.original_stake) - (o_side_position.net_winnings + trade_side_position.net_winnings)

        return (
            <View style={{ borderBottomWidth:1, borderColor:Colors.borders.light, marginTop:3 }}>
                <View type='row' style={{ padding:5 }}>
                    <View style={{ flex:1, flexDirection:'row', alignItems:'center', padding:0 }}>
                        <Button style={{ padding:0 }} onPress={() => onViewPlayer(o_side_order.player_id)}>
                            <Image
                                source={{ uri: o_side_player?.profile_pic && o_side_player.profile_pic != '' ? o_side_player.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                style={{ height:30, width:30, borderRadius:100 }}
                                resizeMode='cover'
                            />
                        </Button>
                        <Button style={{ flex:1, padding:0 }} onPress={() => onViewOrder(o_side_order.order_id)}>
                            <Text theme='h2' textAlign='center'>{o_side_order.title}</Text>
                            <Text theme='description' textAlign='center' style={{ marginTop:3 }}>{o_side_position.original_stake.toFixed(2)} to win {o_side_position.potential_winnings.toFixed(2)}</Text>
                        </Button>
                    </View>
                    <Button float style={{ padding:5, margin:5, justifyContent:'center', alignItems:'center' }} onPress={() => onViewTrade(data.item.trade_id)}>
                        <Text theme='h2' color={Colors.text.action} textAlign='center'>${data.item.trade_amt.toFixed(2)}</Text>
                        <Text theme='description' color={Colors.text.success} style={{ marginTop:3 }} textAlign='center'>{route_premium.toFixed(2)}%</Text>
                    </Button>
                    <View style={{ flex:1, flexDirection:'row', alignItems:'center', padding:0 }}>
                        <Button style={{ flex:1, padding:0 }} onPress={() => onViewOrder(trade_side_order.order_id)}>
                            <Text theme='h2' textAlign='center'>{trade_side_order.title}</Text>
                            <Text theme='description' textAlign='center' style={{ marginTop:3 }}>{trade_side_position.original_stake.toFixed(2)} to win {trade_side_position.potential_winnings.toFixed(2)}</Text>
                        </Button>
                        <Button style={{ padding:0 }} onPress={() => onViewPlayer(trade_side_order.player_id)}>
                            <Image
                                source={{ uri: t_side_player?.profile_pic && t_side_player.profile_pic != '' ? t_side_player.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                style={{ height:30, width:30, borderRadius:100 }}
                                resizeMode='cover'
                            />
                        </Button>
                    </View>
                </View>
                <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                    <View transparent style={{ flex:1, marginRight:5 }}>
                        <Text theme='h2' textAlign='left'>{o_side_position.result_ind?.toUpperCase()}: ${o_side_position.winnings}</Text>
                    </View>
                    <View transparent>
                        <Text theme='h2' color={Colors.text.success} textAlign='center'>${revenue.toFixed(2)}</Text>
                    </View>
                    <View transparent style={{ flex:1, marginLeft:5 }}>
                        <Text theme='h2' textAlign='right'>${trade_side_position.winnings.toFixed(2)} :{trade_side_position.result_ind?.toUpperCase()}</Text>
                    </View>
                </View>
            </View>
        )
    }
    return (
        <View style={{ flex:1 }}>
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h1'>{contest_title}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Trades occuring in this contest and market</Text>
                </View>
                <Button
                    title='X'
                    style={{ padding:10 }}
                    float
                    onPress={() => onClose()}
                />
            </View>
            <View style={{ borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <Pagination 
                    offset={list_data.offset}
                    onNext={() => getTradeData(list_data.offset + 1)}
                    onPrevious={() => getTradeData(list_data.offset - 1)}
                />
            </View>
            <View type='body' style={{flex:1}}>
                 {loading ?
                <ActivityIndicator style={{ padding:20, alignSelf:'center' }} size='large' color={Colors.text.h1} />
                :<></>}
                <FlatList
                    data={trades}
                    key='trade_list'
                    windowSize={4}
                    maxToRenderPerBatch={10}
                    keyExtractor={(item) => item.trade_id.toString()}
                    renderItem={renderTrades}
                />
            </View>
        </View>
    )
}

export default TradeList